import { jobFileUrl, type JobFileLinks, type SourceMeta } from '../../lib/packshots';
import type { StageId } from './railModel';

const NORMALIZED_KEEP_SIDE = 4096;

export type FrameKind = 'original' | 'normalized' | 'erased' | 'cleaned' | 'cutout' | 'output';

export type PreviewFrame = {
  kind: FrameKind;
  label: string;
  url: string;
};

type FrameJob = {
  id: string;
  updatedAt: string | null;
  files: JobFileLinks;
  sourceMeta: Pick<SourceMeta, 'width' | 'height'> | null;
};

const FRAME_LABEL: Record<FrameKind, string> = {
  original: 'Input',
  normalized: 'Normalized',
  erased: 'Remove props',
  cleaned: 'Cleanup',
  cutout: 'Cutout',
  output: 'Output',
};

export function previewFrames(job: FrameJob): PreviewFrame[] {
  const frames: PreviewFrame[] = [];
  const push = (kind: FrameKind, label = FRAME_LABEL[kind]) => {
    if (!job.files[kind]) return;
    frames.push({
      kind,
      label,
      url: jobFileUrl(job.id, kind, job.updatedAt),
    });
  };

  push('original');
  if (keepNormalized(job)) push('normalized');
  else if (!job.files.original) push('normalized', 'Input');
  push('erased');
  push('cleaned');
  push('cutout');
  push('output');
  return frames;
}

export function frameKindForStage(stage: StageId): FrameKind {
  switch (stage) {
    case 'ingest':
    case 'assess':
      return 'original';
    case 'props':
      return 'erased';
    case 'cleanup':
      return 'cleaned';
    case 'cutout':
      return 'cutout';
    case 'compose':
    case 'qa':
      return 'output';
    default: {
      const unreachable: never = stage;
      return unreachable;
    }
  }
}

export function frameIndexForStage(frames: readonly PreviewFrame[], stage: StageId): number {
  const preferred = frameKindForStage(stage);
  const direct = frames.findIndex((frame) => frame.kind === preferred);
  if (direct >= 0) return direct;
  if (preferred === 'original') return frames.findIndex((frame) => frame.kind === 'normalized');
  return -1;
}

function keepNormalized(job: FrameJob): boolean {
  if (!job.files.normalized || !job.files.original || !job.sourceMeta) return false;
  return job.sourceMeta.width >= NORMALIZED_KEEP_SIDE || job.sourceMeta.height >= NORMALIZED_KEEP_SIDE;
}
